import { useEffect, useState, useRef } from 'react';

export type CameraPermissionState = 'prompt' | 'granted' | 'denied' | 'unavailable';

interface UseCameraPermissionReturn {
    permissionState: CameraPermissionState;
    stream: MediaStream | null;
    error: string | null;
    requestPermission: () => Promise<void>;
    stopCamera: () => void;
    isLoading: boolean;
}

/**
 * Custom hook to manage camera permissions and media stream
 */
export const useCameraPermission = (): UseCameraPermissionReturn => {
    const [permissionState, setPermissionState] = useState<CameraPermissionState>('prompt');
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const streamRef = useRef<MediaStream | null>(null);

    /**
     * Check if the browser supports camera access
     */
    const checkCameraSupport = (): boolean => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setPermissionState('unavailable');
            setError('Camera access is not supported in this browser');
            return false;
        }
        return true;
    };

    /**
     * Request camera permission and start stream
     */
    const requestPermission = async (): Promise<void> => {
        if (!checkCameraSupport()) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Request camera access with specific constraints
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: 'user', // Front camera for selfie
                },
                audio: false,
            });

            streamRef.current = mediaStream;
            setStream(mediaStream);
            setPermissionState('granted');
        } catch (err) {
            console.error('Camera access error:', err);

            if (err instanceof Error) {
                if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                    setPermissionState('denied');
                    setError('Camera permission was denied. Please allow camera access in your browser settings.');
                } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                    setPermissionState('unavailable');
                    setError('No camera found on this device.');
                } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                    setPermissionState('unavailable');
                    setError('Camera is already in use by another application.');
                } else {
                    setPermissionState('denied');
                    setError(`Failed to access camera: ${err.message}`);
                }
            } else {
                setPermissionState('denied');
                setError('An unknown error occurred while accessing the camera.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Stop camera stream and release resources
     */
    const stopCamera = (): void => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => {
                track.stop();
            });
            streamRef.current = null;
            setStream(null);
        }
    };

    /**
     * Cleanup on unmount
     */
    useEffect(() => {
        return () => {
            stopCamera();
        };
    }, []);

    /**
     * Check initial permission state (if Permissions API is available)
     */
    useEffect(() => {
        const checkPermission = async () => {
            if ('permissions' in navigator) {
                try {
                    const result = await navigator.permissions.query({ name: 'camera' as PermissionName });

                    if (result.state === 'granted') {
                        setPermissionState('granted');
                    } else if (result.state === 'denied') {
                        setPermissionState('denied');
                    } else {
                        setPermissionState('prompt');
                    }

                    // Listen for permission changes
                    result.onchange = () => {
                        setPermissionState(result.state as CameraPermissionState);
                    };
                } catch (err) {
                    // Permissions API might not support camera query
                    console.log('Permissions API not fully supported');
                }
            }
        };

        checkPermission();
    }, []);

    return {
        permissionState,
        stream,
        error,
        requestPermission,
        stopCamera,
        isLoading,
    };
};
